import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../partage/api/client';
import { cles } from '../../partage/relectures';
import type { TacheDeJournee } from '../../partage/api/types';
import { JOUR_PASSE, majuscule, PLANIFIER_CE_JOUR } from '../../partage/vocabulaire';
import { jourEnToutesLettres } from './datesDeLAgenda';
import { JeChoisis } from './JeChoisis';
import { LaQuestion } from './LaQuestion';
import { reponsesRetenues, type Reponses } from './questionDuJour';
import { ProposeMoi } from './ProposeMoi';
import './LaQuestion.css';

/**
 * Planifier un jour où rien n'est prévu, au téléphone (chantier `telephone-journee-agenda`,
 * ticket 01, décisions d'Igor du 2026-10-05, 7 et 8) : la question, puis l'un des deux choix,
 * qui emporte les réponses.
 *
 * - **Dans l'onglet Journée**, la question se pose sur place, sous « Ta journée » (`plein` faux).
 * - **Depuis l'Agenda**, « Planifier ce jour » la pose en plein écran pour ce jour-là, avec son
 *   retour en haut (`plein`).
 *
 * « Propose-moi une journée » ouvre la proposition (`ProposeMoi`, ticket 02), « Je choisis
 * moi-même » les trois boutons et la liste (`JeChoisis`, ticket 03), partis des mêmes réponses :
 * leur retour revient à la question sans rien écrire, « C'est ma journée » ferme tout. La journée
 * proposée reste une possibilité, jamais imposée (Igor, décision 8).
 */
export function PlanifierUnJour({
  jour,
  plein = false,
  retour,
  phrase,
  faites,
  onFermer,
}: {
  jour: string;
  plein?: boolean;
  /** La phrase de tête de la question, quand une journée existe déjà (« Recommencer », ticket 04). */
  phrase?: string;
  /** Le mot du retour en plein écran (« Agenda »). */
  retour?: string;
  /**
   * Les tâches déjà faites aujourd'hui (« Recommencer », ticket 04) : la journée recommencée les
   * garde à leur heure (spec `maintenant`, « Rouverte aujourd'hui, une journée garde ses tâches faites »).
   */
  faites?: readonly TacheDeJournee[];
  onFermer: (message?: string) => void;
}) {
  const [etape, setEtape] = useState<'question' | 'propose' | 'choisis'>('question');
  const [touchees, setTouchees] = useState<Partial<Reponses>>({});

  // Pour l'heure qu'il est (arrondie par le serveur) et savoir si le jour est aujourd'hui.
  const preparation = useQuery({ queryKey: cles.preparation(jour, 'question'), queryFn: () => api.preparation(jour) });
  const donnees = preparation.data;
  const maintenant = donnees?.maintenant ?? null;
  const aujourdhui = donnees?.jour === donnees?.aujourdhui;
  const passe = donnees ? donnees.jour < donnees.aujourdhui : false;
  const reponses = reponsesRetenues(touchees, maintenant);

  if (etape === 'choisis') {
    return (
      <JeChoisis
        jour={jour}
        reponses={reponses}
        maintenant={maintenant}
        onRetour={() => setEtape('question')}
        onFermer={(message) => onFermer(message)}
        faites={faites}
      />
    );
  }

  if (etape === 'propose') {
    return (
      <ProposeMoi
        jour={jour}
        reponses={reponses}
        maintenant={maintenant}
        onRetour={() => setEtape('question')}
        onFermer={(message) => onFermer(message)}
        faites={faites}
      />
    );
  }

  // La question attend l'heure qu'il est : sans elle, le début par défaut serait faux aujourd'hui.
  const question = !donnees ? null : passe ? (
    <p className="question-phrase">{JOUR_PASSE}</p>
  ) : (
    <LaQuestion
      aujourdhui={aujourdhui}
      phrase={phrase}
      maintenant={maintenant}
      reponses={reponses}
      onRepondre={(r) => setTouchees((avant) => ({ ...avant, ...r }))}
      onProposeMoi={() => setEtape('propose')}
      onJeChoisis={() => setEtape('choisis')}
    />
  );

  if (!plein) return question;

  return (
    <section className="planifier-plein" data-etape="question" aria-label={`${PLANIFIER_CE_JOUR}, ${jourEnToutesLettres(jour)}`}>
      <button type="button" className="planifier-retour" onClick={() => onFermer()}>
        ‹ {retour}
      </button>
      <p className="planifier-sur-titre">{PLANIFIER_CE_JOUR}</p>
      <h2 className="planifier-titre">{majuscule(jourEnToutesLettres(jour))}</h2>
      {question}
    </section>
  );
}
